import { useForm, FormProvider } from "react-hook-form";
import { NewAnnouncementForm } from "./NewAnnouncementForm";
import {
  AnnouncementItem,
  AnnouncementPayload,
  SeverityType,
  TargetGroupType,
} from "./types";
import { DEFAULT_DRAG_HANDLE_SELECTOR } from "../../../app/constants";
import BasicDialog from "../../../common/components/functional/dialogs/BasicDialog";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import useIsLoading from "../../../hooks/useIsLoading";
import { constructPayload, getAnnouncementFormValues } from "./helpers";

type Props = {
  announcement: AnnouncementItem | null;
  open: boolean;
  toggleDialogOpen: (open: boolean) => void;
  handleAddAnnouncement: (item: AnnouncementPayload) => void;
  handleEditAnnouncement: (item: AnnouncementItem) => void;
};

export type FormValues = {
  dateRange: [Date, Date];
  appTypes: TargetGroupType[];
  contentFi: string;
  contentEn: string;
  severity: SeverityType;
  id: string;
  version: number;
};

export const AnnouncementDialog = ({
  announcement,
  open,
  toggleDialogOpen,
  handleAddAnnouncement,
  handleEditAnnouncement,
}: Props) => {
  const { t } = useTranslation("settings");
  const isLoading = useIsLoading(["announcements"]);

  const methods = useForm<FormValues>({
    defaultValues: getAnnouncementFormValues(announcement),
    mode: "onChange",
  });
  const {
    handleSubmit,
    formState: { isValid },
    reset,
  } = methods;

  useEffect(() => {
    if (!open) return;
    reset(getAnnouncementFormValues(announcement));
  }, [open, announcement, reset]);

  const handleSave = (data: FormValues) => {
    const payload = constructPayload(data);
    if (announcement?.id) {
      handleEditAnnouncement({
        ...payload,
        id: data.id,
        version: data.version,
      });
    } else {
      handleAddAnnouncement(payload);
    }
  };

  return (
    <BasicDialog
      open={open}
      onCancel={() => toggleDialogOpen(false)}
      onConfirm={handleSubmit(handleSave)}
      title={
        announcement
          ? t("settings:editAnnouncement")
          : t("settings:addNewAnnouncement")
      }
      confirmLabel={t("common:save")}
      PaperProps={{
        sx: { minWidth: "772px" },
        className: DEFAULT_DRAG_HANDLE_SELECTOR,
      }}
      confirmDisabled={isLoading || !isValid}
      noBorder
      content={
        <FormProvider {...methods}>
          <NewAnnouncementForm />
        </FormProvider>
      }
    />
  );
};
